import { useDeviceDrawerData } from '../../hooks/useDeviceDrawerData'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { usePermissions } from '@/hooks/usePermissions'
import { useTranslation } from 'react-i18next'

import { DrawerTabIProps } from '@/features/device-drawer'
import { DeviceType } from '@/graphql/generated/schemas'

import { AssociatedDevicesInfoModal } from '../../associated-devices-tab/components/AssociatedDevicesInfoModal'
import { AssociatedDevicesTab } from '../../associated-devices-tab/components/AssociatedDevicesTab'
import { DarkDeviceAlerts } from '../components/DarkDeviceAlerts'
import { DarkDeviceAutomationsTab } from '../components/DarkDeviceAutomationsTab'
import { DarkDeviceInfoTab } from '../components/DarkDeviceInfoTab'
import { DarkDeviceLogs } from '../components/DarkDeviceLogs'

export const useDarkDeviceDrawerTabs = ({
  incidents,
  isIncidentsLoading,
  deviceId,
  deviceFloorId,
  deviceName,
  deviceType,
}: {
  incidents: ReturnType<typeof useDeviceDrawerData>['incidents']
  isIncidentsLoading: boolean
  deviceId: string
  deviceFloorId?: string | null
  deviceName?: string | null
  deviceType?: DeviceType
}): DrawerTabIProps[] => {
  const { t } = useTranslation()
  const { isImplementer } = usePermissions()
  const isDeviceAssociationStatusEnabled = useFeatureFlag(
    FeatureFlag.deviceAssociationStatus
  )
  const isDevelopmentModeEnabled = useFeatureFlag(FeatureFlag.development)
  // Both hooks are called unconditionally above so a short-circuit can't
  // change the hook order; the dev flag opens the tab in non-prod envs.
  const isDeviceAutomationsTabEnabled =
    useFeatureFlag(FeatureFlag.deviceAutomationsTab) || isDevelopmentModeEnabled

  const associatedDevicesTab: DrawerTabIProps = {
    label: 'Associated Devices',
    icon: <AssociatedDevicesInfoModal />,
    panel: (
      <AssociatedDevicesTab
        deviceFloorId={deviceFloorId}
        deviceId={deviceId}
        deviceName={deviceName}
        deviceType={deviceType}
      />
    ),
  }

  const recentIncidentsTab: DrawerTabIProps = {
    label: 'Recent Incidents',
    icon: null,
    panel: (
      <DarkDeviceAlerts
        deviceType={deviceType}
        incidents={incidents}
        isLoading={isIncidentsLoading}
      />
    ),
  }

  const automationsTab: DrawerTabIProps = {
    label: t('devices:drawer_tab_automations'),
    icon: null,
    panel: <DarkDeviceAutomationsTab deviceId={deviceId} />,
  }

  const implementerTabs: DrawerTabIProps[] = isImplementer
    ? [
        {
          label: 'Device Info',
          icon: null,
          panel: <DarkDeviceInfoTab id={deviceId} />,
        },
        {
          label: 'Device Logs',
          icon: null,
          panel: <DarkDeviceLogs deviceId={deviceId} />,
        },
      ]
    : []

  return [
    ...(isDeviceAssociationStatusEnabled ? [associatedDevicesTab] : []),
    recentIncidentsTab,
    ...(isDeviceAutomationsTabEnabled ? [automationsTab] : []),
    ...implementerTabs,
  ]
}
